import React from 'react';
import { connect } from 'react-redux';
import _ from 'lodash';
import Promise from 'bluebird';
import DeliveryAddress from './deliveryAddress';
import DeliveryTime from './deliveryTime';
import ShippingOptions from './shippingOptions';
import CustomerInfo from './customerInfo';
import PaymentMethod from './paymentMethod';
import RoundUp from '../../roundUp';
import RoundUpSection from './roundUpSection';
import LegalBox from './legalBox';
import { DangerWarning } from 'scy/Warning';
import { getRecommendations, refreshShippingOptions } from '../../cart/actions';
import {
  submit,
  setOrderId,
  resetDeliveryOptions,
  saveAddress,
  getGiftBag
} from '../actions';
import { loadCards } from 'shared/components/payment/actions';
import { updateUser } from '../../login/actions';
import { isEqual } from 'shared/models/address';
import { trackNow, trackAttribution } from 'shared/models/analytic';
import { trackInfo } from 'shared/models/product';
import { getProducts } from 'shared/models/api';
import PaymentRequestButton from './paymentRequestButton';
import LoadingSpinner from 'shared/components/ui/loadingSpinner';
import { LoadingButton, ConfirmButton } from 'scy/buttons';
import { SmallAlt } from 'scy/text';
import RecommendationsCard from './RecommendationsCard';
import styles from './styles.mod.scss';
import { width100 } from 'scy/utils.mod.scss';
import margin from 'scy/margin.mod.scss';

class CheckoutForm extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      expanded: this.firstEmptySection(props),
      userExists: !this.isEmpty('customerInfo'),
      loading: false,
      error: null,
      paymentRequestLoading: true,
      paymentRequestSelected: false
    };

    this.handleSubmit = this.handleSubmit.bind(this);
    this.handlePaymentRequestToken = this.handlePaymentRequestToken.bind(this);
  }

  componentDidMount() {
    this._trackStarted();
    this._checkForCollections();
    this.props.getRecommendations(this.props.skus);
  }

  componentDidUpdate(prevProps, prevState) {
    const userHasChanged =
      this.props.user.objectId &&
      this.props.user.objectId !== prevProps.user.objectId;
    if (userHasChanged) {
      if (!this.props.orderId) this.props.setOrderId();
      this.props.loadCards();
    }

    if (!isEqual(this.props.address, prevProps.address)) {
      this.props.resetDeliveryOptions();
      this.props.getGiftBag();
    }

    if (this.props.skus.join() !== prevProps.skus.join()) {
      if (this.props.skus.length)
        this.props.getRecommendations(this.props.skus);
      this.clearFormError();
    }

    if (this.state.expanded !== prevState.expanded) {
      if (prevState.expanded) this._trackStepCompleted(prevProps, prevState);
      if (this.state.expanded) this._trackStepViewed(this.props, this.state);
    }

    if (this.props.cart.group_mapping !== prevProps.cart.group_mapping) {
      this._checkForCollections();
    }
  }

  _checkForCollections() {
    const group_mapping = this.props.cart.group_mapping;
    if (!group_mapping) return;
    const collectionSKUs = _.uniq(_.values(group_mapping));
    if (collectionSKUs.length) {
      getProducts(collectionSKUs).then(
        products => (this.collections = products)
      );
    }
  }

  _trackStarted() {
    let params = this._orderParams(this.props);
    params.logged_in = !!this.props.user.objectId;
    params.checkout_steps = this.state.userExists ? 3 : 4;
    params.address_completed =
      this.firstEmptySection(this.props) !== 'deliveryAddress';
    trackNow('Checkout Started', params);
    trackAttribution('Checkout Started', params);
  }

  _trackStepCompleted(props, state) {
    const params = this._stepParams(props, state);
    trackNow('Checkout Step Completed', params);
    trackAttribution('Checkout Completed', params);
  }

  _trackStepViewed(props, state) {
    const params = this._stepParams(props, state);
    trackNow('Checkout Step Viewed', params);
  }

  _trackCompleted() {
    const params = this._orderParams(this.props, { revenue: true });
    trackNow('Order Completed', params);
    trackAttribution('Order Completed', params);
    if (this.collections) this._trackCollectionsPurchased();
    window.branch.track('checkout', {
      order_id: this.props.id,
      parse_id: this.props.user.id || this.props.user.objectId
    });
    if (params.firstTimePurchaser) {
      trackNow('First Purchase');
      trackAttribution('First Purchase');
      window.branch.track('First Purchase');
    }
  }

  _trackCollectionsPurchased() {
    const collections = this.collections || [];
    collections.forEach(collection => {
      const items = collection.ingredients
        .map(ingredient => {
          const cartItems = this.props.cart.products || [];
          return _.find(cartItems, item =>
            _.includes(ingredient.sku_list, item.sku_id)
          );
        })
        .filter(item => !!item);

      if (!items.length) return;

      trackNow('Collection Purchased', {
        title: collection.title,
        subtitle: collection.subtitle,
        volume: collection.volume,
        sku_id: collection.sku_id,
        price: items.reduce((total, item) => total + item.price, 0),
        items: items
      });
    });
  }

  _orderParams(props, opts = {}) {
    const vendor = props.vendor;
    const cart = props.cart;
    const promo = cart.promo;
    const shipping = props.address.shipping
      ? cart.shipping_fee
      : cart.delivery_fee;
    let params = {
      affiliation: vendor.store_id,
      subtotal: cart.subtotal,
      total: cart.total,
      order_id: props.id,
      shipping: shipping,
      tax: cart.tax,
      discount: promo && promo.value_type !== 'credit' ? promo.val : 0,
      coupon: promo && promo.value_type !== 'credit' ? promo.code : '',
      credit:
        promo && promo.value_type === 'credit' ? cart.total_discount : null,
      currency: 'USD',
      products: (cart.products || []).map(item => {
        return trackInfo(item, item.qty);
      }),
      giftOrder: props.isGift
    };
    if (opts.revenue) {
      params.revenue = cart.total;
    }
    if (props.user && props.user.orders) {
      params.firstTimePurchaser = props.user.orders.length === 0;
    }
    if (props.paymentMethod && props.paymentMethod.brand) {
      params.payment_method = props.paymentMethod.brand;
    } else if (props.user && props.user.defSource) {
      params.payment_method = props.user.defSource.brand;
    }
    return params;
  }

  _stepParams(props, state) {
    let step;
    let step_name;
    switch (state.expanded) {
      case 'deliveryAddress':
        step = 1;
        step_name = 'Delivery Address';
        break;
      case 'deliveryTime':
        step = 2;
        step_name = 'Delivery Time';
        break;
      case 'shippingOptions':
        step = 2;
        step_name = 'Shipping Options';
        break;
      case 'customerInfo':
        step = 3;
        step_name = 'Customer Info';
        break;
      case 'paymentMethod':
        step = this.state.userExists ? 3 : 4;
        step_name = 'Payment Method';
        break;
    }

    let params = {
      checkout_id: props.id,
      step,
      step_name
    };

    if (!_.isEmpty(props.paymentMethod))
      params.payment_method = props.paymentMethod.brand;
    if (!_.isEmpty(props.shippingOption))
      params.shipping_method = props.shippingOption.name;

    return params;
  }

  expand(section) {
    this.setState({ expanded: section });
  }

  collapse() {
    const nextExpanded = this.firstEmptySection(this.props);
    this.setState({ expanded: nextExpanded });
    return null;
  }

  firstEmptySection(props) {
    const products = props.cart.products || [];
    if (!products.length) return null;

    const sections = [
      'deliveryAddress',
      props.address.shipping ? 'shippingOptions' : 'deliveryTime',
      'customerInfo',
      'paymentMethod'
    ];

    return _.find(sections, section => this.isEmpty(section)) || null;
  }

  isEmpty(section) {
    switch (section) {
      case 'deliveryAddress':
        return (
          _.isEmpty(this.props.address.address) ||
          _.isEmpty(this.props.address.zip)
        );
      case 'deliveryTime':
        return _.isEmpty(this.props.deliveryTime);
      case 'shippingOptions':
        return _.isEmpty(this.props.shippingOption);
      case 'customerInfo':
        return (
          _.isEmpty(this.props.user) ||
          _.isEmpty(this.props.user.firstname) ||
          _.isEmpty(this.props.user.lastname) ||
          _.isEmpty(this.props.user.birthday) ||
          _.isEmpty(this.props.user.email) ||
          _.isEmpty(this.props.user.mobile) ||
          !this.props.user.mobileVerified
        );
      case 'paymentMethod':
        return _.isEmpty(this.props.paymentMethod);
      default:
        return false;
    }
  }

  canCheckOut() {
    return !this.state.expanded && !!(this.props.skus || []).length;
  }

  clearFormError() {
    this.setState({ error: null });
  }

  _validate() {
    const err = msg => Promise.reject(new Error(msg));

    if (this._isImmediateDelivery() && !this._isOpen()) {
      return err(
        "Saucey isn't accepting orders for immediate delivery right now."
      );
    }

    if (this._cartHasUnavailableProducts()) {
      return err(
        "Some of the items in your cart aren't available at this location. Please remove them before continuing."
      );
    }

    if (this._cartIsEmpty()) {
      return err('Your cart is empty.');
    }

    return Promise.resolve();
  }

  _isImmediateDelivery() {
    return !this.props.deliveryTime.start && !this.props.shippingOption;
  }

  _isOpen() {
    let vendor = this.props.vendor;
    let open = vendor.hours.open * 1000;
    let close = vendor.hours.close * 1000;
    let now = Date.now();
    return now >= open && now < close;
  }

  _cartHasUnavailableProducts() {
    return !!this.props.cart.products.filter(p => p.price == null || p.soldout)
      .length;
  }

  _cartIsEmpty() {
    return !this.props.cart.products.filter(p => !!p.price).length;
  }

  _skus(products) {
    return products
      .filter(p => !!p.price)
      .reduce(
        (skus, p) => ({
          ...skus,
          [p.sku_id]: p.qty
        }),
        {}
      );
  }

  handleSubmit(e) {
    e.preventDefault();
    return this._checkout(this.props.paymentMethod);
  }

  handlePaymentRequestToken(token, onSuccess, onFail) {
    return this._checkout({ token }, onSuccess, onFail);
  }

  _checkout(paymentMethod, onSuccess, onFail) {
    this.setState({ loading: true, error: null });

    return this._validate()
      .then(() => {
        let params = {
          skus: this._skus(this.props.cart.products),
          address: this.props.address,
          phone: this.props.user.mobile
        };

        if (paymentMethod.token) {
          params.stripeToken = paymentMethod.token;
        } else {
          params.cardId = paymentMethod.id;
        }

        if (this.props.deliveryTime.start)
          params.scheduled = this.props.deliveryTime.start;
        if (this.props.cart.promo) {
          if (this.props.cart.promo.value_type === 'credit') {
            params.credit = this.props.cart.promo;
          } else {
            params.promo = this.props.cart.promo.code;
          }
        }
        if (this.props.shippingOption)
          params.shipping_name = this.props.shippingOption.name;
        if (this.props.isGift) {
          let gift = {
            name: this.props.giftRecipient,
            phone: this.props.giftMobile
          };
          if (this.props.giftEmail) gift.email = this.props.giftEmail;
          if (this.props.giftNote) gift.message = this.props.giftNote;
          if (this.props.giftOccasion) gift.occasion = this.props.giftOccasion;
          params.gift = gift;
        }

        params.roundUp = this.props.roundUp;
        if (this.props.roundUp) {
          params.roundUpOrgID = this.props.roundUpOrgID;
        }
        return this.props.submit(params, onSuccess, onFail);
      })
      .then(() => this._trackCompleted())
      .then(() => this.props.saveAddress())
      .then(() => (window.location = '/tracking'))
      .catch(({ message }) =>
        this.setState({ loading: false, error: message })
      );
  }

  render() {
    const canCheckOut = this.canCheckOut();
    const showPaymentRequestButton =
      _.isEmpty(this.props.paymentMethod) ||
      this.props.paymentMethod.id === 'paymentRequest';
    return (
      <>
        <DeliveryAddress
          isExpanded={this.state.expanded === 'deliveryAddress'}
          canEdit={!this.isEmpty('deliveryAddress')}
          expand={() => this.expand('deliveryAddress')}
          collapse={() => this.collapse('deliveryAddress')}
        />
        {this.props.address.shipping ? (
          <ShippingOptions
            isExpanded={this.state.expanded === 'shippingOptions'}
            canEdit={!this.isEmpty('shippingOptions')}
            expand={() => this.expand('shippingOptions')}
            collapse={() => this.collapse('shippingOptions')}
          />
        ) : (
          <DeliveryTime
            isExpanded={this.state.expanded === 'deliveryTime'}
            canEdit={!this.isEmpty('deliveryTime')}
            expand={() => this.expand('deliveryTime')}
            collapse={() => this.collapse('deliveryTime')}
          />
        )}
        {!this.state.userExists && (
          <CustomerInfo
            isExpanded={this.state.expanded === 'customerInfo'}
            canEdit={!this.isEmpty('customerInfo')}
            expand={() => this.expand('customerInfo')}
            collapse={() => this.collapse('customerInfo')}
          />
        )}
        <PaymentMethod
          isExpanded={this.state.expanded === 'paymentMethod'}
          canEdit={!this.isEmpty('paymentMethod')}
          expand={() => this.expand('paymentMethod')}
          collapse={() => this.collapse('paymentMethod')}
        />

        <RoundUpSection>
          <RoundUp
            isCheckoutPage={true}
            updateUser={data => this.props.updateUser(data)}
          />
        </RoundUpSection>

        <RecommendationsCard
          recommendations={this.props.recommendations}
          onClick={this.props.openQuickView}
        />
        <div className={`${margin.sm} ${margin.tablet_all0}`}>
          {this.state.error && (
            <DangerWarning>{this.state.error}</DangerWarning>
          )}

          {this.props.cart &&
            this.props.cart.legal &&
            this.props.cart.legal.display_text && (
              <LegalBox {...this.props.cart.legal} />
            )}
        </div>
        {canCheckOut &&
          (showPaymentRequestButton ? (
            <div className={styles.formSubmit}>
              <Disclaimer />
              <PaymentRequestButton
                disabled={
                  !canCheckOut ||
                  this.state.loading ||
                  this.props.loading ||
                  this.props.cart.loading
                }
                onToken={this.handlePaymentRequestToken}
              />
              {(this.state.loading || this.props.loading) && (
                <LoadingSpinner black />
              )}
            </div>
          ) : (
            <form onSubmit={this.handleSubmit} className={styles.formSubmit}>
              <Disclaimer />
              <LoadingButton
                Button={ConfirmButton}
                type="submit"
                className={width100}
                disabled={
                  this.state.loading ||
                  this.props.loading ||
                  this.props.cart.loading
                }
                loading={
                  this.state.loading ||
                  this.props.loading ||
                  this.props.cart.loading
                }
              >
                Place Order
              </LoadingButton>
            </form>
          ))}
      </>
    );
  }
}

function Disclaimer() {
  return (
    <SmallAlt
      className={styles.small}
      marginClass={`${margin.top_sm} ${margin.bottom_md}`}
    >
      Someone 21 years or older must be present upon delivery with a valid
      government issued ID.
    </SmallAlt>
  );
}

const ConnectedCheckoutForm = connect(
  state => ({
    ...state.checkout,
    address: state.address,
    user: state.login.user || {},
    cart: state.cart,
    skus: (state.cart.products || []).map(p => p.sku_id),
    loadingRecommendations: state.cart.loadingRecommendations,
    recommendations: state.cart.recommendations || [],
    roundUp:
      state.roundUp &&
      state.roundUp.userOrg &&
      state.roundUp.userOrg.roundUpEnabled
        ? state.roundUp.userOrg.roundUpEnabled
        : false,
    roundUpOrgID:
      state.roundUp &&
      state.roundUp.userOrg &&
      state.roundUp.userOrg.roundUpOrgId
        ? state.roundUp.userOrg.roundUpOrgId
        : ''
  }),
  {
    submit,
    setOrderId,
    resetDeliveryOptions,
    saveAddress,
    getRecommendations,
    refreshShippingOptions,
    getGiftBag,
    loadCards,
    updateUser
  }
)(CheckoutForm);

export default ConnectedCheckoutForm;
